import { useEffect, useState } from "react";
import axios from "axios";
import {
  Building2,
  FileText,
  Globe,
  Image as ImageIcon,
  Save,
  Plus,
  X,
} from "lucide-react";
import { canEditCompanyInfo } from "../../utils/permissions";
import { API_URL } from "../../api/api";
import { UPLOAD_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import { toast } from "sonner";
import {
  SettingsCard,
  SettingsField,
  SettingsInput,
  SettingsSelect,
} from "./settingsUi";

const COUNTRY_STATE_MAP = {
  India: ["Kerala", "Tamil Nadu", "Karnataka", "Delhi"],
  USA: ["California", "Texas", "New York"],
  Canada: ["Ontario", "Quebec"],
};
const CURRENCIES = [
  { code: "INR", label: "₹ INR – Indian Rupee" },
  { code: "USD", label: "$ USD – US Dollar" },
  { code: "EUR", label: "€ EUR – Euro" },
  { code: "GBP", label: "£ GBP – British Pound" },
  { code: "AUD", label: "$ AUD – Australian Dollar" },
  { code: "CAD", label: "$ CAD – Canadian Dollar" },
  { code: "SGD", label: "$ SGD – Singapore Dollar" },
];
const TIMEZONES = [
  { value: "Asia/Kolkata", label: "(GMT+05:30) Mumbai, Kolkata, New Delhi" },
  { value: "Asia/Dubai", label: "(GMT+04:00) Dubai" },
  { value: "Europe/London", label: "(GMT+00:00) London" },
  { value: "Europe/Berlin", label: "(GMT+01:00) Berlin" },
  { value: "America/New_York", label: "(GMT-05:00) New York" },
  { value: "America/Chicago", label: "(GMT-06:00) Chicago" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) Los Angeles" },
  { value: "Asia/Singapore", label: "(GMT+08:00) Singapore" },
  { value: "Australia/Sydney", label: "(GMT+10:00) Sydney" },
];

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 };
const textareaStyle = {
  width: "100%",
  padding: "11px 14px",
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  font: `400 14px ${T.font}`,
  color: T.fg1,
  outline: "none",
  boxSizing: "border-box",
  background: "var(--em-bg-card)",
  minHeight: 80,
  resize: "vertical",
};

const CompanyInformation = () => {
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");
  const editable = canEditCompanyInfo(role);

  const baseURL = `${API_URL}/api/settings/company-info`;
  const baseimg = `${UPLOAD_URL}`;
  const [form, setForm] = useState({
    company_name: "",
    address: "",
    country: "",
    state: "",
    city: "",
    timezone: "",
    currency: "",
    gst_number: "",
    pan_number: "",
    tan_number: "",
    pf_number: "",
    esic_number: "",
    lin_number: "",
    registration_certificate_number: "",
    website: "",
    social_media: "",
  });

  const [logo, setLogo] = useState(null);
  const [signature, setSignature] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [signaturePreview, setSignaturePreview] = useState("");
  const [socialLinks, setSocialLinks] = useState([{ platform: "", url: "" }]);

  /* ================= FETCH ================= */
  useEffect(() => {
    axios
      .get(baseURL, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data || {};
        setForm((prev) => ({ ...prev, ...data }));
        if (data.company_logo) setLogoPreview(baseimg + data.company_logo);
        if (data.authorized_signature)
          setSignaturePreview(baseimg + data.authorized_signature);
        if (data.social_media) {
          if (typeof data.social_media === "string") {
            setSocialLinks(JSON.parse(data.social_media));
          } else if (Array.isArray(data.social_media)) {
            setSocialLinks(data.social_media);
          } else {
            setSocialLinks([]);
          }
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (form.country === "India" && !form.currency) {
      setForm((prev) => ({ ...prev, currency: "INR" }));
    }
  }, [form.country]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "country") {
      setForm((prev) => ({ ...prev, country: value, state: "" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    setSignature(file);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const addSocialLink = () =>
    setSocialLinks([...socialLinks, { platform: "", url: "" }]);
  const removeSocialLink = (index) =>
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  const updateSocialLink = (index, field, value) => {
    const updated = [...socialLinks];
    updated[index][field] = value;
    setSocialLinks(updated);
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    const fd = new FormData();
    Object.keys(form).forEach((k) => fd.append(k, form[k]));
    if (socialLinks) fd.append("social_media", JSON.stringify(socialLinks));
    if (logo) fd.append("company_logo", logo);
    if (signature) fd.append("authorized_signature", signature);
    await axios.put(baseURL, fd, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });
    toast.error("Company information updated");
  };

  const LEGAL_FIELDS = [
    { name: "gst_number", label: "GST Number", ph: "GST Number" },
    { name: "pan_number", label: "PAN Number", ph: "PAN Number" },
    { name: "tan_number", label: "TAN Number", ph: "TAN Number" },
    { name: "pf_number", label: "PF Number", ph: "PF Number" },
    { name: "esic_number", label: "ESI Number", ph: "ESIC Number" },
    { name: "lin_number", label: "LIN Number", ph: "LIN Number" },
    {
      name: "registration_certificate_number",
      label: "Registration Certificate Number",
      ph: "CIN / Registration Certificate Number",
      full: true,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* BASIC INFO */}
      <SettingsCard
        icon={Building2}
        title="Basic Information"
        subtitle="Core company details and location"
      >
        <div style={grid2}>
          <SettingsField label="Company Name">
            <SettingsInput
              name="company_name"
              value={form.company_name}
              onChange={handleChange}
              disabled={!editable}
              placeholder="Company Name"
            />
          </SettingsField>
          <SettingsField label="Address">
            <textarea
              name="address"
              value={form.address || ""}
              onChange={handleChange}
              disabled={!editable}
              placeholder="Address"
              style={textareaStyle}
            />
          </SettingsField>
          <SettingsField label="Country">
            <SettingsSelect
              name="country"
              value={form.country}
              onChange={handleChange}
              disabled={!editable}
            >
              <option value="">Select Country</option>
              {Object.keys(COUNTRY_STATE_MAP).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
          <SettingsField label="State / Province">
            <SettingsSelect
              name="state"
              value={form.state}
              onChange={handleChange}
              disabled={!editable || !form.country}
            >
              <option value="">Select State</option>
              {(COUNTRY_STATE_MAP[form.country] || []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
          <SettingsField label="District / City">
            <SettingsInput
              name="city"
              value={form.city}
              onChange={handleChange}
              disabled={!editable}
              placeholder="City"
            />
          </SettingsField>
          <SettingsField label="Timezone">
            <SettingsSelect
              name="timezone"
              value={form.timezone}
              onChange={handleChange}
              disabled={!editable}
            >
              <option value="">Select timezone</option>
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
          <SettingsField label="Currency">
            <SettingsSelect
              name="currency"
              value={form.currency}
              onChange={handleChange}
              disabled={!editable}
            >
              <option value="">Select currency</option>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
        </div>
      </SettingsCard>

      {/* LEGAL & REGISTRATION DETAILS */}
      <SettingsCard
        icon={FileText}
        title="Legal & Registration Details"
        subtitle="Regulatory and compliance information"
      >
        <div style={grid2}>
          {LEGAL_FIELDS.map((f) => (
            <SettingsField
              key={f.name}
              label={f.label}
              style={f.full ? { gridColumn: "1 / -1" } : undefined}
            >
              <SettingsInput
                name={f.name}
                placeholder={f.ph}
                value={form[f.name] || ""}
                onChange={handleChange}
                disabled={!editable}
              />
            </SettingsField>
          ))}
        </div>
      </SettingsCard>

      {/* ONLINE PRESENCE */}
      <SettingsCard
        icon={Globe}
        title="Online Presence"
        subtitle="Website and social media links"
      >
        <SettingsField label="Website URL">
          <SettingsInput
            name="website"
            value={form.website}
            onChange={handleChange}
            disabled={!editable}
            placeholder="Website URL"
          />
        </SettingsField>

        <div
          style={{
            font: `600 13px ${T.font}`,
            color: T.fg1,
            margin: "4px 0 10px",
          }}
        >
          Social Media
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {socialLinks.map((row, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 10, alignItems: "center" }}
            >
              <SettingsInput
                placeholder="Platform (LinkedIn, X, Facebook)"
                value={row.platform}
                disabled={!editable}
                onChange={(e) =>
                  updateSocialLink(i, "platform", e.target.value)
                }
                style={{ flex: "0 0 240px" }}
              />
              <SettingsInput
                placeholder="URL"
                value={row.url}
                disabled={!editable}
                onChange={(e) => updateSocialLink(i, "url", e.target.value)}
                style={{ flex: 1 }}
              />
              {editable && (
                <button
                  onClick={() => removeSocialLink(i)}
                  style={{
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    borderRadius: 9,
                    border: `1px solid ${T.border}`,
                    background: "var(--em-bg-card)",
                    cursor: "pointer",
                    color: T.danger,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={15} />
                </button>
              )}
            </div>
          ))}
          {editable && (
            <button
              onClick={addSocialLink}
              style={{
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                borderRadius: 8,
                border: `1px solid ${T.borderSoft}`,
                background: "var(--em-bg-card)",
                color: T.fg1,
                font: `500 13px ${T.font}`,
                cursor: "pointer",
                marginTop: 2,
              }}
            >
              <Plus size={15} /> Add Social Link
            </button>
          )}
        </div>
      </SettingsCard>

      {/* BRANDING */}
      <SettingsCard
        icon={ImageIcon}
        title="Branding Assets"
        subtitle="Company logo and signature"
      >
        <div style={grid2}>
          <SettingsField label="Company Logo">
            {logoPreview && (
              <img
                src={logoPreview}
                alt="Logo"
                style={{
                  maxHeight: 90,
                  marginBottom: 10,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  padding: 8,
                  background: "var(--em-bg-card)",
                  display: "block",
                }}
              />
            )}
            {editable && (
              <input
                type="file"
                onChange={handleLogoChange}
                style={{ font: `400 13px ${T.font}` }}
              />
            )}
          </SettingsField>
          <SettingsField label="Authorized Signature">
            {signaturePreview && (
              <img
                src={signaturePreview}
                alt="Signature"
                style={{
                  maxHeight: 90,
                  marginBottom: 10,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  padding: 8,
                  background: "var(--em-bg-card)",
                  display: "block",
                }}
              />
            )}
            {editable && (
              <input
                type="file"
                onChange={handleSignatureChange}
                style={{ font: `400 13px ${T.font}` }}
              />
            )}
          </SettingsField>
        </div>
      </SettingsCard>

      {/* SAVE BAR */}
      {editable && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            background: T.bgCard,
            border: `1px solid ${T.orangeEdge}`,
            borderRadius: T.rXl,
            padding: "18px 24px",
            boxShadow: "0 6px 16px -10px rgba(245,75,1,0.4)",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>
              Ready to save changes?
            </div>
            <div
              style={{ font: `400 13px ${T.font}`, color: T.fg5, marginTop: 2 }}
            >
              All configurations will be updated
            </div>
          </div>
          <button
            onClick={handleSave}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 20px",
              borderRadius: 10,
              border: 0,
              cursor: "pointer",
              background: T.gradient,
              color: "#fff",
              font: `700 14px ${T.font}`,
              boxShadow: T.shadowBrand,
            }}
          >
            <Save size={16} /> Save Changes
          </button>
        </div>
      )}
    </div>
  );
};

export default CompanyInformation;
